/**
 * AI Entry Classifier
 *
 * Classifies field log entries into diagnostic categories that feed Otis
 * (Techery's internal AI assistant). Categories mirror how senior techs think:
 * observation → hypothesis → action → result, plus ruled_out and decision
 * for the meta-reasoning, with note as a fallback.
 *
 * Two entry points:
 *  1. suggestEntryType(text) — single-entry suggestion shown as a chip below
 *     each new entry while a job is in progress (lightweight, optional).
 *  2. classifyEntries(entries) — batch pass run at job completion to
 *     pre-label any untagged entries before the tech reviews them.
 *
 * Both hit Claude with a tight, deterministic schema. On API failure they
 * fall back to a keyword heuristic so the tech can still proceed offline.
 */
import type { FieldLogEntry, FieldLogEntryType } from '@/types';
import { CLAUDE_MODEL_HAIKU } from './ai-config';
import { callClaudeViaProxy } from './claude.client';

const ENTRY_TYPES: FieldLogEntryType[] = [
  'observation',
  'hypothesis',
  'action',
  'result',
  'ruled_out',
  'decision',
  'note',
];

const ENTRY_TYPE_DESCRIPTIONS: Record<FieldLogEntryType, string> = {
  observation: 'What the tech sees, hears, or measures (signal levels, error lights, customer-reported behaviour).',
  hypothesis: 'What the tech thinks might be wrong / what they suspect — a guess being explored.',
  action: 'Something the tech did or tried (rebooted, swapped, ran a cable, called the office).',
  result: 'What happened after an action — outcome, change, fix confirmed or not confirmed.',
  ruled_out: 'A path the tech investigated and dismissed — "it\'s not the cable", "router is fine".',
  decision: 'A deliberate choice, often outside the original scope (added an AP, swapped equipment, changed approach).',
  note: 'Generic or contextual — fallback when the entry is just a comment, customer detail, or doesn\'t fit the others.',
};

// ---------------------------------------------------------------------------
// Heuristic fallback (offline / API failure)
// ---------------------------------------------------------------------------

const HEURISTIC_RULES: { type: FieldLogEntryType; patterns: RegExp[] }[] = [
  {
    type: 'ruled_out',
    patterns: [
      /\bnot the\b/i,
      /\bisn'?t the\b/i,
      /\bruled out\b/i,
      /\bis fine\b/i,
      /\bworks fine\b/i,
      /\bno fault\b/i,
      /\bchecks out\b/i,
    ],
  },
  {
    type: 'decision',
    patterns: [
      /\bdecided to\b/i,
      /\bgoing to\b/i,
      /\bswapped\b/i,
      /\badded\b/i,
      /\bchanged approach\b/i,
      /\bupgraded\b/i,
      /\binstead of\b/i,
    ],
  },
  {
    type: 'hypothesis',
    patterns: [
      /\bmight be\b/i,
      /\bsuspect\b/i,
      /\bcould be\b/i,
      /\bprobably\b/i,
      /\blikely\b/i,
      /\bthink it'?s\b/i,
      /\bmaybe\b/i,
    ],
  },
  {
    type: 'result',
    patterns: [
      /\bnow working\b/i,
      /\bfixed\b/i,
      /\bresolved\b/i,
      /\bafter that\b/i,
      /\bcame back online\b/i,
      /\bback up\b/i,
      /\bworking again\b/i,
      /\bsuccess\b/i,
    ],
  },
  {
    type: 'action',
    patterns: [
      /\brebooted?\b/i,
      /\brestarted?\b/i,
      /\bran a\b/i,
      /\bran the\b/i,
      /\bcalled\b/i,
      /\binstalled?\b/i,
      /\bmounted?\b/i,
      /\bconnected?\b/i,
      /\bconfigured?\b/i,
      /\breplaced?\b/i,
      /\btested?\b/i,
    ],
  },
  {
    type: 'observation',
    patterns: [
      /\bcustomer (says|said|reports|reported)\b/i,
      /\bsignal is\b/i,
      /\bspeed test\b/i,
      /\b\d+\s?(mbps|dbm|db)\b/i,
      /\bshowing\b/i,
      /\blights? are\b/i,
      /\bnoticed\b/i,
      /\bsee that\b/i,
    ],
  },
];

/** Keyword-based fallback classifier. Returns 'note' when nothing matches. */
export function heuristicClassify(text: string): FieldLogEntryType {
  const trimmed = (text || '').trim();
  if (!trimmed) return 'note';
  for (const rule of HEURISTIC_RULES) {
    if (rule.patterns.some((p) => p.test(trimmed))) {
      return rule.type;
    }
  }
  return 'note';
}

// ---------------------------------------------------------------------------
// Claude-backed classifier
// ---------------------------------------------------------------------------

const SYSTEM_PROMPT = `You classify short field-tech log entries into one of seven diagnostic categories. The categories capture HOW a tech is thinking on-site, not WHAT the work is. Pick the single best label.

Categories:
${ENTRY_TYPES.map((t) => `- ${t}: ${ENTRY_TYPE_DESCRIPTIONS[t]}`).join('\n')}

Rules:
- Pick exactly ONE category from the list.
- If genuinely ambiguous, prefer "note".
- Respond with the category word ONLY — no quotes, no punctuation, no explanation.`;

async function callClaude(userMessage: string, model: string): Promise<string> {
  const { text } = await callClaudeViaProxy({
    model,
    maxTokens: 256,
    temperature: 0, // deterministic classification into the fixed category set
    system: SYSTEM_PROMPT,
    userContent: userMessage,
  });
  return text;
}

function normalizeType(raw: string): FieldLogEntryType {
  const lower = (raw || '').toLowerCase().trim().replace(/[^a-z_]/g, '');
  if (ENTRY_TYPES.includes(lower as FieldLogEntryType)) {
    return lower as FieldLogEntryType;
  }
  return 'note';
}

/**
 * Suggest a category for a single field log entry.
 * Used by FieldLogModule for the live "suggested chip" UX.
 *
 * Falls back to the heuristic on API failure or missing key, so the chip
 * still shows something useful offline.
 */
export async function suggestEntryType(text: string): Promise<FieldLogEntryType> {
  const trimmed = (text || '').trim();
  if (!trimmed) return 'note';

  try {
    const raw = await callClaude(`Classify this entry: ${trimmed}`, CLAUDE_MODEL_HAIKU);
    return normalizeType(raw);
  } catch (err) {
    console.warn('[ai-entry-classifier] suggest fallback:', err);
    return heuristicClassify(trimmed);
  }
}

/**
 * Batch-classify untagged entries at job completion.
 *
 * Returns the same array with `entryType` + `entryTypeSource: 'ai_suggested'`
 * filled in for any entries that were missing them. Already-tagged entries
 * pass through unchanged.
 */
export async function classifyEntries(entries: FieldLogEntry[]): Promise<FieldLogEntry[]> {
  if (!entries.length) return entries;

  const untagged = entries
    .map((e, idx) => ({ entry: e, idx }))
    .filter(({ entry }) => !entry.entryType);
  if (untagged.length === 0) return entries;

  let labels: FieldLogEntryType[] = [];
  try {
    const numbered = untagged
      .map(({ entry }, i) => `${i + 1}. ${entry.text.replace(/\n/g, ' ')}`)
      .join('\n');
    const userMessage = `Classify each of these field log entries. Respond with one category word per line, in order, no numbering, nothing else.\n\n${numbered}`;
    const raw = await callClaude(userMessage, CLAUDE_MODEL_HAIKU);
    labels = raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map(normalizeType);
  } catch (err) {
    console.warn('[ai-entry-classifier] batch fallback to heuristic:', err);
  }

  // Fill any missing labels (API line-count mismatch or fallback) with heuristic.
  const result = entries.slice();
  untagged.forEach(({ entry, idx }, i) => {
    const label = labels[i] ?? heuristicClassify(entry.text);
    result[idx] = {
      ...entry,
      entryType: label,
      entryTypeSource: 'ai_suggested',
    };
  });
  return result;
}

/** Display label for a category — used in chips and review UI. */
export const ENTRY_TYPE_LABELS: Record<FieldLogEntryType, string> = {
  observation: 'Observation',
  hypothesis: 'Hypothesis',
  action: 'Action',
  result: 'Result',
  ruled_out: 'Ruled out',
  decision: 'Decision',
  note: 'Note',
};

/** Tailwind colour classes per category — keep visual encoding consistent. */
export const ENTRY_TYPE_COLORS: Record<FieldLogEntryType, { bg: string; text: string; border: string }> = {
  observation: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  hypothesis: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  action: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  result: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  ruled_out: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  decision: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  note: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

export { ENTRY_TYPES };
